/* Shared Teams UI atoms. Every class string here is a design-system recipe —
   nothing invented. Anything used by more than one Teams panel lives here so
   the same element never gets two different treatments. */

import React from "react";
import { Check, X, Minus } from "lucide-react";

import type { LucideIcon } from "lucide-react";

/* One audited check on a team receipt. Declared here rather than pulled from
   `shared/types`: `CheckRow` renders whatever a caller hands it, and the two
   callers (the receipt dialog and the replay panel) build these rows locally. */
interface TeamCheck {
  ok?: boolean;
  label?: React.ReactNode;
  detail?: React.ReactNode;
  evidence?: React.ReactNode;
}

/* ------------------------------- status chip ------------------------------- */

const TONES = {
  success: "bg-[var(--success-bg)] text-[var(--success)] ring-[var(--success-ring)]",
  warn: "bg-[var(--warn-bg)] text-[var(--warn-text)] ring-[var(--warn-ring)]",
  danger: "bg-[var(--danger-bg)] text-[var(--danger)] ring-[var(--danger-ring)]",
  info: "bg-[var(--info-bg)] text-[var(--info-text)] ring-[var(--info-ring)]",
  neutral: "bg-[var(--surface-2)] text-[var(--text-secondary)] ring-[var(--hairline-strong)]",
};

export function Chip({ tone = "neutral", className = "", children }: {
  tone?: keyof typeof TONES;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <span
      className={`rounded-full text-[12px] font-medium px-2 py-[2px] ring-1 whitespace-nowrap ${TONES[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

/* Uppercase micro tag — the mono variant from the system. */
export function Tag({ className = "", children }: { className?: string; children?: React.ReactNode }) {
  return (
    <span
      className={`inline-flex items-center h-[18px] px-[7px] rounded-full text-[11px] font-medium bg-[var(--surface-2)] text-[var(--text-secondary)] ${className}`}
    >
      {children}
    </span>
  );
}

/* ---------------------------------- money ---------------------------------- */

/* Sub-cent amounts are the norm here, so a naive $0.00 would read as free.
   Numbers are always mono + tabular per the system. */
export const fmtUsd = (n: number | null | undefined): string => {
  if (n == null || !Number.isFinite(n)) return "Unavailable";
  if (n === 0) return "$0";
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
};

/* "1 fixtures" reads as a bug to anyone evaluating the product. */
export const plural = (n: number, one: string, many: string = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/* Milliseconds under a second, seconds under a minute, then two whole units: a run
   held an hour for approval reads "1h 00m", not "3600.0s" overflowing its tile. */
export const fmtMs = (ms: number | null | undefined): string => {
  if (ms == null || Number.isNaN(ms)) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 59_950) return `${(ms / 1000).toFixed(1)}s`; // 59.95s would round to "60.0s"
  const s = Math.round(ms / 1000);
  const [d, h, m] = [Math.floor(s / 86_400), Math.floor(s / 3600) % 24, Math.floor(s / 60) % 60];
  const two = (n: number) => String(n).padStart(2, "0");
  return d ? `${d}d ${h}h` : h ? `${h}h ${two(m)}m` : `${m}m ${two(s % 60)}s`;
};

export const Money = ({ value, className = "" }: { value: number | null | undefined; className?: string }) => (
  <span className={`font-mono tabular-nums ${className}`}>{fmtUsd(value)}</span>
);

/* -------------------------------- check row -------------------------------- */

/* The auditable unit. One check, its verdict, and the evidence it judged —
   never a bare pass/fail, because a bare pass/fail is exactly what buyers are
   refusing to accept from vendors on outcome-priced invoices. */
export function CheckRow({ check, dense = false }: { check: TeamCheck; dense?: boolean }) {
  const ok = check.ok;
  return (
    <div className={`flex items-start gap-2 ${dense ? "py-1.5" : "py-2"}`}>
      <span
        className={`shrink-0 mt-[1px] w-[15px] h-[15px] rounded-full ring-1 flex items-center justify-center ${
          ok
            ? "bg-[var(--success-bg)] ring-[var(--success-ring)] text-[var(--success)]"
            : "bg-[var(--danger-bg)] ring-[var(--danger-ring)] text-[var(--danger)]"
        }`}
      >
        {ok ? <Check className="w-[9px] h-[9px]" strokeWidth={2.5} />
            : <X className="w-[9px] h-[9px]" strokeWidth={2.5} />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-[var(--text-primary)] tracking-[-0.005em] break-words">
          {check.label}
        </div>
        {check.detail && (
          <div className="text-[12px] text-[var(--text-tertiary)] mt-[1px]">{check.detail}</div>
        )}
        {check.evidence && (
          <div className="mt-1 px-2 py-1 rounded-[8px] bg-[var(--surface-2)] ring-1 ring-[var(--hairline)] font-mono text-[11.5px] text-[var(--text-secondary)] break-words whitespace-pre-wrap">
            {check.evidence}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------- field / label ----------------------------- */

export function Field({ label, hint, children, className = "" }: {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <div className="section-label">{label}</div>
      {children}
      {hint && <div className="text-[12px] text-[var(--text-tertiary)] leading-[1.5]">{hint}</div>}
    </div>
  );
}

/* Full-width single control (the common case). */
export const inputCls =
  "w-full px-2.5 h-8 rounded-[8px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] focus:ring-[var(--hairline-focus)] text-[13px] outline-none placeholder:text-[var(--text-quaternary)] text-[var(--text-primary)]";

/* Same control without a width, for use inside a flex row where the row —
   not the control — decides how space is shared. */
export const controlCls =
  "px-2.5 h-8 min-w-0 rounded-[8px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] focus:ring-[var(--hairline-focus)] text-[13px] outline-none placeholder:text-[var(--text-quaternary)] text-[var(--text-primary)]";

export const textareaCls =
  "w-full px-2.5 py-2 rounded-[8px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] focus:ring-[var(--hairline-focus)] text-[13px] outline-none resize-none placeholder:text-[var(--text-quaternary)] text-[var(--text-primary)] leading-[1.6]";

/* -------------------------------- segmented -------------------------------- */

export function Segmented<T extends string | number>({ value, onChange, options, role, label, className = "" }: {
  value: T;
  onChange: (value: T) => void;
  options: Array<{ value: T; label: React.ReactNode }>;
  /** Tabs that switch what shows below, or one choice of a set (a filter); plain toggle buttons without. */
  role?: "tablist" | "radiogroup";
  /** What the group is, for a screen reader; given with `role`. */
  label?: string;
  className?: string;
}) {
  const state = (on: boolean) =>
    role === "tablist" ? { role: "tab", "aria-selected": on }
    : role === "radiogroup" ? { role: "radio", "aria-checked": on }
    : { "aria-pressed": on };
  return (
    <div
      role={role}
      aria-label={label}
      className={`inline-flex shrink-0 p-[3px] rounded-full bg-[var(--surface-2)] ${className}`}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          {...state(value === o.value)}
          onClick={() => onChange(o.value)}
          className={`press inline-flex items-center gap-1.5 whitespace-nowrap px-2.5 py-[4px] rounded-full text-[12px] font-medium ${
            value === o.value
              ? "bg-[var(--surface)] text-[var(--text-primary)] ring-1 ring-[var(--hairline)] shadow-lift"
              : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* --------------------------------- toggle ---------------------------------- */

export function Toggle({ checked, onChange, label, hint }: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: React.ReactNode;
  hint?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="press w-full flex items-start gap-2.5 text-left"
    >
      <span
        className={`shrink-0 mt-[1px] w-[30px] h-[18px] rounded-full ring-1 transition-colors duration-200 flex items-center px-[2px] ${
          checked
            ? "bg-[var(--brand)] ring-[var(--brand)]"
            : "bg-[var(--surface-2)] ring-[var(--hairline-strong)]"
        }`}
      >
        <span
          className="w-[14px] h-[14px] rounded-full bg-[var(--surface)] shadow-[0_1px_2px_rgba(0,0,0,0.15)]"
          style={{ transform: `translateX(${checked ? 12 : 0}px)`, transition: "transform .2s var(--easing)" }}
        />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] text-[var(--text-primary)] tracking-[-0.005em]">{label}</span>
        {hint && <span className="block text-[12px] text-[var(--text-tertiary)] mt-[1px] leading-[1.5]">{hint}</span>}
      </span>
    </button>
  );
}

/* -------------------------------- empty ------------------------------------ */

export function Empty({ icon: Icon, title, sub }: {
  icon: LucideIcon;
  title?: React.ReactNode;
  sub?: React.ReactNode;
}) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-3 fade-in py-10">
      <div className="w-10 h-10 rounded-[12px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] flex items-center justify-center">
        <Icon className="w-[18px] h-[18px] text-[var(--text-tertiary)]" strokeWidth={1.5} />
      </div>
      <div className="text-[15px] font-medium tracking-[-0.005em]">{title}</div>
      {sub && <div className="text-[13px] text-[var(--text-tertiary)] mt-1 max-w-[320px] text-center">{sub}</div>}
    </div>
  );
}

export { Minus };
